import React, { useState, useEffect } from "react";
import {
  FiSearch,
  FiFilter,
  FiUser,
  FiArrowLeft,
  FiMessageSquare,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import {
  BsWhatsapp,
  BsThreeDotsVertical,
  BsCheck2All,
  BsExclamationCircle,
  BsCheck,
} from "react-icons/bs";
import apiEndpoints from "../../apiconfig";

const LiveChat = () => {
  const [chats, setChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileView, setIsMobileView] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [chatsPerPage] = useState(10);
  const [messagesCurrentPage, setMessagesCurrentPage] = useState(1);
  const [messagesPerPage] = useState(15);
  const [wabaNumber, setWabaNumber] = useState("");
  // Responsive screen detection
  useEffect(() => {
    const checkScreen = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobileView(mobile);
      if (!mobile) setShowChat(false);
    };

    checkScreen();
    window.addEventListener("resize", checkScreen);

    // ✅ Load WABA number from localStorage safely
    const storedWaba = localStorage.getItem("waba_number");
    if (storedWaba) {
      setWabaNumber(storedWaba);
    }

    // ✅ Fetch chat list
    const token = localStorage.getItem("token");

    fetch(`${apiEndpoints.chat_history}?history=24hrs`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setChats(data.data);
        }
      })
      .catch((err) => {
        console.error("Fetch error:", err);
      });

    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  // ✅ Select chat & load messages
  const handleChatSelect = (chat) => {
    setSelectedChat(chat);
    setMessagesCurrentPage(1); // Reset to first page when selecting new chat

    const token = localStorage.getItem("token");

    fetch(
      `${apiEndpoints.chat_messages}?history=24hrs&mobile_number=${chat.mobile_number}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setMessages(data.data);
        }
      })
      .catch((err) => {
        console.error("Message fetch error:", err);
      });

    if (isMobileView) setShowChat(true);
  };

  const handleBackToChatList = () => {
    setShowChat(false);
    setSelectedChat(null);
    setMessages([]);
    setCurrentPage(1);
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300); // 300ms debounce

    return () => clearTimeout(delay);
  }, [searchQuery]);

  // Filter chats based on search
  const filteredChats = chats.filter(
    (chat) =>
      chat.contact_name?.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
      chat.mobile_number?.includes(debouncedQuery)
  );

  // Format timestamp
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Pagination for chats
  const indexOfLastChat = currentPage * chatsPerPage;
  const indexOfFirstChat = indexOfLastChat - chatsPerPage;
  const currentChats = filteredChats.slice(indexOfFirstChat, indexOfLastChat);
  const totalChatPages = Math.ceil(filteredChats.length / chatsPerPage);

  // Pagination for messages
  const indexOfLastMessage = messagesCurrentPage * messagesPerPage;
  const indexOfFirstMessage = indexOfLastMessage - messagesPerPage;
  const currentMessages = messages.slice(
    indexOfFirstMessage,
    indexOfLastMessage
  );
  const totalMessagePages = Math.ceil(messages.length / messagesPerPage);

  const nextChatPage = () => {
    if (currentPage < totalChatPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevChatPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const nextMessagesPage = () => {
    if (messagesCurrentPage < totalMessagePages) {
      setMessagesCurrentPage(messagesCurrentPage + 1);
    }
  };

  const prevMessagesPage = () => {
    if (messagesCurrentPage > 1) {
      setMessagesCurrentPage(messagesCurrentPage - 1);
    }
  };

const renderTemplateMessage = (msg) => {
  const payload = msg.template_payload;

  // ✅ Fallback for old messages
  if (!payload || !payload.body || !payload.body.text) {
    return (
      <p className="text-white text-sm whitespace-pre-wrap">{msg.message}</p>
    );
  }

  let renderedText = payload.body.text;

  // ✅ Replace variables {{1}}, {{2}} ...
  if (Array.isArray(payload.variables)) {
    payload.variables.forEach((v, i) => {
      renderedText = renderedText.replace(`{{${i + 1}}}`, v);
    });
  }

  return (
    <div className="space-y-2">
      {/* HEADER */}
      {payload.header?.type === "text" && (
        <p className="font-semibold text-white">{payload.header.text}</p>
      )}

      {payload.header?.type === "image" && (
        <img
          src={payload.header.link}
          alt="template-header"
          className="rounded-lg max-w-full"
        />
      )}

      {/* BODY */}
      <p className="text-white text-sm whitespace-pre-wrap">{renderedText}</p>

      {/* BUTTONS */}
      {Array.isArray(payload.buttons) && payload.buttons.length > 0 && (
        <div className="flex flex-col gap-2 mt-2">
          {payload.buttons.map((btn, i) => {
            if (btn.type === "URL") {
              return (
                <a
                  key={i}
                  href={btn.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-[#25D366] text-center text-sm font-medium py-2 rounded-lg"
                >
                  {btn.text}
                </a>
              );
            }

            if (btn.type === "QUICK_REPLY") {
              return (
                <button
                  key={i}
                  className="bg-white/20 text-white text-sm py-2 rounded-lg"
                >
                  {btn.text}
                </button>
              );
            }

            return null;
          })}
        </div>
      )}
    </div>
  );
};


  // Chat List Component
const ChatList = React.memo(() => (

    <div className="w-full lg:w-96 bg-white border-r border-gray-200 flex flex-col h-full font-['Montserrat'] shadow-sm">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1f8c55] to-[#25D366] text-white px-6 py-5">
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-xl font-semibold font-['Montserrat']">
            Business Inbox
          </h1>
          <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
            <span className="text-xs font-medium font-['Montserrat']">
              LIVE
            </span>
          </div>
        </div>
        <p className="text-white/80 text-sm font-['Montserrat']">
          WABA: {wabaNumber}
        </p>
      </div>

      {/* Search Bar */}
      <div className="p-4 bg-gray-50/80 border-b border-gray-200">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
          <input
            type="text"
            placeholder="Search conversations..."
            className="w-full pl-10 pr-10 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#25D366] focus:border-transparent outline-none transition-all font-['Montserrat']"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {/* <FiFilter className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer" /> */}
        </div>
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto bg-white">
        {currentChats.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-gray-400 py-12">
            <FiMessageSquare className="text-4xl mb-3 opacity-50" />
            <p className="text-sm font-medium font-['Montserrat']">
              No conversations found
            </p>
          </div>
        ) : (
          <>
            {currentChats.map((chat) => (
              <div
                key={chat.id}
                onClick={() => handleChatSelect(chat)}
                className={`p-4 cursor-pointer border-b border-gray-100 transition-all duration-200 ${
                  selectedChat?.id === chat.id
                    ? "bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-[#25D366]"
                    : "hover:bg-gray-50"
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#25D366] to-[#1f8c55] rounded-full flex items-center justify-center flex-shrink-0">
                    <FiUser className="text-white text-lg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="font-semibold text-gray-900 truncate text-sm font-['Montserrat']">
                        {chat.contact_name || chat.mobile_number}
                      </h3>
                      <span className="text-xs text-gray-500 whitespace-nowrap ml-2 font-['Montserrat']">
                        {chat.last_message_time
                          ? formatTime(chat.last_message_time)
                          : ""}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 truncate leading-relaxed font-['Montserrat']">
                      {chat.last_message || "Start a conversation"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Chat List Pagination */}
      {filteredChats.length > chatsPerPage && (
        <div className="border-t border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={prevChatPage}
              disabled={currentPage === 1}
              className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium font-['Montserrat'] ${
                currentPage === 1
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              <FiChevronLeft className="mr-1" />
              Previous
            </button>

            <span className="text-sm text-gray-700 font-medium font-['Montserrat']">
              Page {currentPage} of {totalChatPages}
            </span>

            <button
              onClick={nextChatPage}
              disabled={currentPage === totalChatPages}
              className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium font-['Montserrat'] ${
                currentPage === totalChatPages
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              Next
              <FiChevronRight className="ml-1" />
            </button>
          </div>
        </div>
      )}
    </div>
));

  // Chat View Component
  const ChatView = () => (
    <div className="flex-1 flex flex-col bg-white h-full font-['Montserrat']">
      {selectedChat ? (
        <>
          {/* Chat Header */}
          <div className="px-6 py-4 bg-white border-b border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {isMobileView && (
                  <button
                    onClick={handleBackToChatList}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <FiArrowLeft className="text-gray-600 text-xl" />
                  </button>
                )}
                <div className="w-12 h-12 bg-gradient-to-br from-[#25D366] to-[#1f8c55] rounded-full flex items-center justify-center">
                  <FiUser className="text-white text-lg" />
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900 text-lg font-['Montserrat']">
                    {selectedChat.contact_name || selectedChat.mobile_number}
                  </h2>
                  <div className="flex items-center space-x-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    <p className="text-xs text-gray-500 font-['Montserrat']">
                      Online • WhatsApp Business
                    </p>
                  </div>
                </div>
              </div>
              <button className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600">
                <BsThreeDotsVertical className="text-xl" />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 p-6 overflow-y-auto bg-gradient-to-b from-gray-50 to-white">
            {currentMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <div className="bg-gray-100 p-6 rounded-2xl mb-4">
                  <BsWhatsapp className="text-gray-300 text-4xl" />
                </div>
                <p className="text-sm font-medium text-gray-500 font-['Montserrat']">
                  No messages yet
                </p>
                <p className="text-xs text-gray-400 mt-1 font-['Montserrat']">
                  Start the conversation
                </p>
              </div>
            ) : (
              <div className="max-w-4xl mx-auto space-y-4">
                {currentMessages.map((msg) => (
                  <div key={msg.id} className="flex justify-end">
                    <div className="max-w-xs lg:max-w-md rounded-2xl px-4 py-3 bg-gradient-to-r from-[#25D366] to-[#1f8c55] shadow-sm">
                      {msg.message_type === "template" &&
                      msg.template_payload ? (
                        renderTemplateMessage(msg)
                      ) : (
                        <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">
                          {msg.message}
                        </p>
                      )}

                      <div className="flex items-center justify-end space-x-1 mt-2">
                        <span className="text-xs text-white/70 font-['Montserrat']">
                          {formatTime(msg.timestamp)}
                        </span>
                        {msg.status === "failed" ? (
                          <BsExclamationCircle
                            className="text-red-500 text-sm"
                            title="Failed"
                          />
                        ) : msg.status === "delivered" ? (
                          <BsCheck
                            className="text-white/70 text-sm"
                            title="Delivered"
                          />
                        ) : msg.status === "read" ? (
                          <BsCheck2All
                            className="text-white/70 text-sm"
                            title="Read"
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Messages Pagination */}
          {messages.length > messagesPerPage && (
            <div className="border-t border-gray-200 bg-white px-6 py-3">
              <div className="flex items-center justify-between">
                <button
                  onClick={prevMessagesPage}
                  disabled={messagesCurrentPage === 1}
                  className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium font-['Montserrat'] ${
                    messagesCurrentPage === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <FiChevronLeft className="mr-2" />
                  New Messages
                </button>

                <span className="text-sm text-gray-700 font-medium font-['Montserrat']">
                  Page {messagesCurrentPage} of {totalMessagePages}
                </span>

                <button
                  onClick={nextMessagesPage}
                  disabled={messagesCurrentPage === totalMessagePages}
                  className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium font-['Montserrat'] ${
                    messagesCurrentPage === totalMessagePages
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Old Messages
                  <FiChevronRight className="ml-2" />
                </button>
              </div>
            </div>
          )}

          {/* Conversation Footer */}
          <div className="px-6 py-4 bg-white border-t border-gray-200">
            <div className="text-center">
              <p className="text-xs text-gray-400 font-medium font-['Montserrat']">
                This conversation is secured with WhatsApp Business API
              </p>
            </div>
          </div>
        </>
      ) : (
        // Empty State
        <div className="flex-1 flex flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-white p-8">
          <div className="bg-gradient-to-br from-[#25D366] to-[#1f8c55] p-8 rounded-3xl shadow-lg mb-6">
            <BsWhatsapp className="text-white text-5xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center font-['Montserrat']">
            Business Communication Console
          </h2>
          <p className="text-gray-500 text-center max-w-md leading-relaxed font-['Montserrat']">
            Select a conversation from the sidebar to view messages and manage
            customer communications.
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`bg-gray-50 font-['Montserrat'] ${
        isMobileView ? "h-screen" : "h-[calc(100vh-125px)]"
      }`}
    >
      {isMobileView ? (
        <div className="flex flex-col h-full">
          {showChat ? <ChatView /> : <ChatList />}
        </div>
      ) : (
        <div className="flex h-full rounded-2xl overflow-hidden shadow-lg border border-gray-200 m-4">
          <ChatList />
          <ChatView />
        </div>
      )}
    </div>
  );
};

export default LiveChat;
